import React, {useRef} from "react";
import {useSelector} from "react-redux";
import {Store} from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import {snowplowTrackEvent} from "utils/snowplow";
import {getArchiveCallbackUrl} from "utils/api/api";
import {launchDarkly} from "utils/launchDarkly";
import translate from "components/translations/translations.wrapper";
import {Button, ButtonType} from "components/common/Button.component";


interface DownloadButtonsProps {
    translate?: (key: string) => string;
}

export const DownloadButtons = ({translate}: DownloadButtonsProps): React.ReactElement => {
    const downloadElement = useRef<HTMLAnchorElement>(null);

    const downloadLinks = useSelector((store: Store) => reduxSelectors.getAttachmentsDownloadLinks(store));
    const isPreviewDetached = useSelector((store: Store) => reduxSelectors.isDocumentPreviewDetached(store));
    const userData = useSelector((store: Store) => reduxSelectors.getUsersData(store));

    const useNewUrlsForArchive = launchDarkly.userClient?.variation("fe-use-new-urls-for-archive", false);

    const trackDownloadEvent = (label: string) => {
        if (isPreviewDetached) {
            snowplowTrackEvent({
                category: "task_detail",
                action: "detach-window",
                label,
                value: "true",
            }, userData);
        }
    };

    const downloadArchiveUsingCallback = (callbackUrl: string | null) => {
        trackDownloadEvent("download-attachment");
        getArchiveCallbackUrl(callbackUrl).then((result: { url: string; }) => {
            if (result.url && downloadElement.current) {
                downloadElement.current.href = result.url;
                downloadElement.current.click();
            }
        });
    };

    const downloadDirectly = (url: string, trackLabel: string) => {
        if (downloadElement.current) {
            downloadElement.current.href = url;
            downloadElement.current.click();
        }
        trackDownloadEvent(trackLabel);
    };

    const renderDownloadButton = (
        callbackUrl: string | null,
        directUrl: string | null,
        text: string,
        titleKey: string,
        testId: string,
        trackLabel: string
    ) => {
        const shouldUseCallback = useNewUrlsForArchive && callbackUrl;
        const hasUrl = shouldUseCallback || directUrl;

        if (!hasUrl) return null;

        return (
            <Button
                type={ButtonType.GHOST}
                onClick={() => shouldUseCallback
                    ? downloadArchiveUsingCallback(callbackUrl)
                    : downloadDirectly(directUrl!, trackLabel)
                }
                iconOnly={false}
                text={text}
                dataTestId={testId}
                tooltip={{id: testId + "-tooltip", content: () => translate(titleKey)?.toString()}}
            />
        );
    };

    return (
        <>
            <a href="" target={isPreviewDetached? "_blank" : "_self"} download="" hidden={true} ref={downloadElement} data-testid="__tst_downloadElement"/>

            {renderDownloadButton(
                downloadLinks.downloadZIPCallbackUrl,
                downloadLinks.downloadZIPUrl,
                translate("taskDetail.downloadAll"),
                "taskDetail.download",
                "__tst_downloadAllAttachments",
                "download-attachment"
            )}

            {renderDownloadButton(
                downloadLinks.downloadPDFCallbackUrl,
                downloadLinks.downloadPDFUrl,
                translate("taskDetail.print"),
                "taskDetail.print",
                "__tst_printPDF",
                "print-attachment"
            )}
        </>
    );
}

export default translate(DownloadButtons);

